import { Dialog } from "@mui/material";
import { useLanguage } from "locale/useLanguage";
import { KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { NavIcon, NavIconName } from "./icons";
import { NavGroup } from "./useNavigation";

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
export const shortcutLabel = isMac ? "⌘K" : "Ctrl K";

type Entry = {
  key: string;
  section: "pages" | "families";
  title: string;
  meta: string;
  path: string;
  icon: NavIconName;
};

// minuscolo e senza accenti, per trovare "misu" in "Misù"
const normalize = (value: string) =>
  value.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

type MenuSearchProps = {
  open: boolean;
  onClose: () => void;
  groups: NavGroup[];
  families: { id: string; name: string }[];
};

function MenuSearch({ open, onClose, groups, families }: MenuSearchProps) {
  const [t] = useLanguage();
  const history = useHistory();
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const entries = useMemo<Entry[]>(() => {
    const pages: Entry[] = groups.flatMap((group) =>
      group.items.flatMap((item) => [
        { key: item.path, section: "pages", title: item.title, meta: group.title, path: item.path, icon: item.icon },
        ...(item.children ?? [])
          .filter((child) => child.path !== item.path)
          .map((child) => ({
            key: child.path,
            section: "pages" as const,
            title: child.title,
            meta: item.title,
            path: child.path,
            icon: item.icon,
          })),
      ]),
    );
    const catalog = groups.flatMap((g) => g.items).find((i) => i.id === "products");
    const familyEntries: Entry[] = catalog
      ? families.map((family) => ({
          key: `family-${family.id}`,
          section: "families",
          title: family.name,
          meta: t("navigation.catalog"),
          path: `/products/${family.id}`,
          icon: "products",
        }))
      : [];
    return [...pages, ...familyEntries];
  }, [groups, families, t]);

  const results = useMemo(() => {
    const tokens = normalize(query).split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return entries.filter((e) => e.section === "pages");
    return entries.filter((e) => {
      const haystack = normalize(`${e.title} ${e.meta}`);
      return tokens.every((token) => haystack.includes(token));
    });
  }, [entries, query]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
    }
  }, [open]);

  useEffect(() => setActiveIndex(0), [query]);

  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const go = (entry?: Entry) => {
    if (!entry) return;
    onClose();
    history.push(entry.path);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(results[activeIndex]);
    }
  };

  const sections: Entry["section"][] = ["pages", "families"];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ className: "menu-search", "aria-label": t("navigation.search.button") } as any}
      sx={{ "& .MuiDialog-container": { alignItems: "flex-start" } }}
    >
      <div className="menu-search__field">
        <NavIcon name="search" />
        <input
          autoFocus
          type="text"
          role="combobox"
          aria-expanded="true"
          aria-controls="menu-search-list"
          aria-activedescendant={results[activeIndex] ? `menu-search-opt-${activeIndex}` : undefined}
          aria-autocomplete="list"
          aria-label={t("navigation.search.button")}
          placeholder={t("navigation.search.placeholder")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <kbd>ESC</kbd>
      </div>

      <div className="menu-search__list" id="menu-search-list" role="listbox" ref={listRef}>
        {results.length === 0 && (
          <p className="menu-search__empty">{t("navigation.search.empty", { query })}</p>
        )}
        {sections.map((section) => {
          const items = results
            .map((entry, index) => ({ entry, index }))
            .filter(({ entry }) => entry.section === section);
          if (items.length === 0) return null;
          return (
            <div key={section} role="group" aria-label={t(`navigation.search.${section}`)}>
              <span className="menu-search__section">{t(`navigation.search.${section}`)}</span>
              {items.map(({ entry, index }) => (
                <div
                  key={entry.key}
                  id={`menu-search-opt-${index}`}
                  data-index={index}
                  role="option"
                  aria-selected={index === activeIndex}
                  className={`menu-search__option ${index === activeIndex ? "is-active" : ""}`}
                  onMouseMove={() => setActiveIndex(index)}
                  onClick={() => go(entry)}
                >
                  <NavIcon name={entry.icon} />
                  <span className="menu-search__title">{entry.title}</span>
                  <span className="menu-search__meta">{entry.meta}</span>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      <div className="menu-search__footer">
        <span>{t("navigation.search.hint_navigate")}</span>
        <span>{t("navigation.search.hint_open")}</span>
        <span>{t("navigation.search.hint_shortcut", { shortcut: shortcutLabel })}</span>
      </div>
    </Dialog>
  );
}

export default MenuSearch;
